/*
 * Copyright (C) 2023 Nick Robinson
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

package uk.co.nickthecoder.glok.backend.webgl

import kotlinx.browser.window
import org.khronos.webgl.WebGLRenderingContext
import org.khronos.webgl.WebGLRenderingContext.Companion.BLEND
import org.khronos.webgl.WebGLRenderingContext.Companion.ONE_MINUS_SRC_ALPHA
import org.khronos.webgl.WebGLRenderingContext.Companion.SCISSOR_TEST
import org.khronos.webgl.WebGLRenderingContext.Companion.SRC_ALPHA
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.events.WheelEvent
import uk.co.nickthecoder.glok.application.GlokSettings
import uk.co.nickthecoder.glok.backend.*
import uk.co.nickthecoder.glok.event.Event
import uk.co.nickthecoder.glok.event.Key
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.scene.MousePointer
import uk.co.nickthecoder.glok.util.*
import kotlin.reflect.KClass
import org.w3c.dom.events.MouseEvent as DomMouseEvent

/**
 * The backend rendering code for the web (i.e. in a web browser).
 * Unlike the JVM backend (GLBackend), the WebGLBackend only support a single instance of [Window],
 * which uses a html `canvas`.
 *
 * Major difference between WebGL and a regular GL frame buffer :
 *
 *     A WebGL canvas has transparency by default
 *     A WebGL canvas uses pre-multiplied alphas
 *
 * I really don't want to deal with pre-multiplied alphas, so I've turned this off.
 *
 * I've read that turning off alpha gives large a performance hit, so I've left it on.
 * Instead, within [clear], we ensure the fill color is fully opaque and then turn off the alpha channel
 * with a color mask. I don't know if this is any quicker than turning off alpha completely.
 */
class WebGLBackend : Backend {

    private var scale: Float = 1f

    override var viewMatrix = Matrix.IDENTITY
        private set

    /**
     * Transforms from Glok logical units from the perspective of the transformed nodes to
     * Glok logical units relative to the scene.
     * i.e. after the transformation is applied, we have Glok logical units, where (0,0) is the top left of
     * the window.
     */
    private var currentTransformation: Matrix? = null
    private var plainViewMatrix: Matrix? = null

    private val clipStack = mutableListOf<ClipRegion>()

    private var inView = false

    // region ==== Init ====
    init {
        // We can ONLY use overlay stages, as we cannot create new windows.
        GlokSettings.useOverlayStages = true

        val canvas = window.document.getElementById("glokCanvas") as? HTMLCanvasElement
        if (canvas == null) {
            log.error("HTML <canvas id=\"glokCanvas\"> not found")
            throw GlokException("HTML <canvas id=\"glokCanvas\"> not found")
        } else {
            WebGLBackend.canvas = canvas

            // I don't know how to use the Kotlin wrapper function and pass additional arguments to getContext.
            // I want the equivalent of this  js code :
            //    gl = canvas.getContext("webgl", { premultipliedAlpha: false } )
            // So instead, I'm using eval and passing in the javascript code required. Grr.
            val jscode =
                "window.document.getElementById(\"glokCanvas\").getContext( \"webgl\", { premultipliedAlpha: false } )"
            gl = eval(jscode) as WebGLRenderingContext

            log.info("initialised WebGLBackend")
        }
    }

    // endregion init

    // region ==== View ====

    override fun drawFrame(window: Window, block: () -> Unit) {
        //log.info("drawFrame begin")
        window as WebGLWindow
        gl.viewport(0, 0, window.width, window.height)
        block()
        //log.info("drawFrame end")
        gl.flush()
    }


    override fun drawView(width: Float, height: Float, scale: Float, block: () -> Unit) {
        if (inView) {
            log.error("drawView already started")
        }
        inView = true
        this.scale = scale

        viewMatrix = Matrix(
            2f / width, 0f,
            0f, 2f / - height,
            - 1f, 1f
        )

        try {
            reset()
            gl.colorMask(true, true, true, false)
            block()
        } finally {
            reset()
            inView = false
        }
    }

    override fun setMousePointer(window: Window, cursor: MousePointer) {
        log.warn("setMousePointer Not yet implemented")
    }

    // endregion View

    // region ==== Clipping and Transforms ====

    override fun beginClipping(left: Float, top: Float, width: Float, height: Float): Boolean {
        val window = webGLWindow ?: throw GlokException("beginClipping before window is initialised")

        // Values left,top, width, height are in Glok's logical units.
        // Values with a "scaled" prefix are in physical pixels.

        val scaledLeft = left * scale
        val scaledWidth = width * scale
        val scaledHeight = height * scale
        val scaledBottom = window.height - top * scale - scaledHeight
        val currentTransformation: Matrix? = currentTransformation
        val requestedClipRegion = if (currentTransformation == null) {
            // This is vastly more common than the "else" branch.
            ClipRegion(scaledLeft.toInt(), scaledBottom.toInt(), scaledWidth.toInt(), scaledHeight.toInt())
        } else {
            // There's a transformation, so the clipping has to be transformed too.
            // So let's transform each of our 4 corners into scene coordinates (still in Glok logical units),
            // with (0,0) being the top left of the window.

            val t0 = currentTransformation.times(left, top)
            val t1 = currentTransformation.times(left + width, top)
            val t2 = currentTransformation.times(left + width, top + height)
            val t3 = currentTransformation.times(left, top + height)

            // Note, the final clipping may be a LARGER area than the untransformed one if there is a rotation,
            // as clipping uses axis aligned rectangles.
            val tLeft = min(min(t0.first, t1.first), min(t2.first, t3.first))
            val tRight = max(max(t0.first, t1.first), max(t2.first, t3.first))
            val tTop = min(min(t0.second, t1.second), min(t2.second, t3.second))
            val tBottom = max(max(t0.second, t1.second), max(t2.second, t3.second))

            // Now convert them into physical pixel coordinates, with (0,0) being the bottom left.
            val tScaledLeft = tLeft * scale
            val tScaledWidth = (tRight - tLeft) * scale
            val tScaledHeight = (tBottom - tTop) * scale
            val tScaledBottom = window.height - tTop * scale - tScaledHeight

            ClipRegion(tScaledLeft.toInt(), tScaledBottom.toInt(), tScaledWidth.toInt(), tScaledHeight.toInt())
        }


        val newClipRegion = if (clipStack.isNotEmpty()) {
            requestedClipRegion.intersection(clipStack.last())
        } else {
            requestedClipRegion
        }
        if (newClipRegion.width <= 0 || newClipRegion.height <= 0) return false

        if (clipStack.isEmpty()) {
            gl.enable(SCISSOR_TEST)
        }
        clipStack.add(newClipRegion)
        gl.scissor(newClipRegion.left, newClipRegion.bottom, newClipRegion.width, newClipRegion.height)

        // To debug clipping, draw a HUGE rectangle, and only the clipped portion should be displayed.
        // fillRect(-10000f, -100000f, 10000f, 10000f, Color.RED.opacity(0.5f))

        return true
    }

    override fun endClipping() {
        if (clipStack.isEmpty()) {
            throw GlokException("Unmatched beginClipping / endClipping")
        } else {
            clipStack.removeLast()
            if (clipStack.isEmpty()) {
                gl.disable(SCISSOR_TEST)
            } else {
                val clipRegion = clipStack.last()
                gl.scissor(clipRegion.left, clipRegion.bottom, clipRegion.width, clipRegion.height)
            }
        }
    }

    override fun noClipping(block: () -> Unit) {
        gl.disable(SCISSOR_TEST)
        block()
        if (clipStack.isNotEmpty()) {
            gl.enable(SCISSOR_TEST)
        }
    }

    override fun transform(transformation: Matrix) {
        if (currentTransformation != null) plainViewMatrix = viewMatrix
        val oldTransformation = currentTransformation
        currentTransformation =
            if (oldTransformation == null) transformation else oldTransformation * transformation
        viewMatrix *= transformation
    }

    override fun clearTransform() {
        currentTransformation = null
        plainViewMatrix?.let { viewMatrix = it }
    }

    override fun transform(transformation: Matrix, block: () -> Unit) {
        val oldMatrix = viewMatrix
        val oldTransformation = currentTransformation
        currentTransformation =
            if (oldTransformation == null) transformation else oldTransformation * transformation
        viewMatrix *= transformation
        try {
            block()
        } finally {
            viewMatrix = oldMatrix
            currentTransformation = oldTransformation
        }
    }

    // endregion Clipping and Transforms

    // region ==== Drawing ====

    /**
     * Note, unlike the GLBackend, we are drawing to a frame buffer which has an alpha channel.
     * But to make it appear the same, when we clear, we set the color mask to prevent writing to the alpha channel.
     *
     * NOTE : The `colorMask` afterward is `colorMask(true, true, true, false)`.
     */
    override fun clear(color: Color) {
        with(gl) {
            // The clear operation is the only operation which writes to the alpha channel.
            colorMask(true, true, true, true)
            clearColor(color.red, color.green, color.blue, color.alpha)
            clear(WebGLRenderingContext.COLOR_BUFFER_BIT)
            colorMask(true, true, true, false)
        }
    }

    override fun fillRect(left: Float, top: Float, right: Float, bottom: Float, color: Color) {
        FlatColorProgram.use(viewMatrix, color) {
            fillRect(left, top, right, bottom)
        }
    }

    override fun strokeInsideRect(
        left: Float, top: Float, right: Float, bottom: Float,
        insideLeft: Float, insideTop: Float, insideRight: Float, insideBottom: Float,
        color: Color
    ) {
        FlatColorProgram.use(viewMatrix, color) {
            strokeInsideRect(
                left, top, right, bottom,
                insideLeft, insideTop, insideRight, insideBottom
            )
        }
    }

    override fun fillCircle(centerX: Float, centerY: Float, radius: Float, color: Color) {
        FillCircleProgram.use(viewMatrix, color, radius) {
            fillCircle(centerX, centerY, radius)
        }
    }

    override fun fillQuarterCircle(
        centerX: Float, centerY: Float, cornerX: Float, cornerY: Float,
        color: Color, radius: Float
    ) {
        FillCircleProgram.use(viewMatrix, color, radius) {
            fillQuarterCircle(radius, centerX, centerY, cornerX, cornerY)
        }
    }

    override fun strokeCircle(centerX: Float, centerY: Float, radius: Float, innerRadius: Float, color: Color) {
        StrokeCircleProgram.use(viewMatrix, color, radius, innerRadius) {
            strokeCircle(centerX, centerY, radius)
        }
    }

    override fun strokeQuarterCircle(
        centerX: Float, centerY: Float, cornerX: Float, cornerY: Float,
        color: Color, radius: Float, innerRadius: Float
    ) {
        StrokeCircleProgram.use(viewMatrix, color, radius, innerRadius) {
            strokeQuarterCircle(radius, centerX, centerY, cornerX, cornerY)
        }
    }

    override fun drawTexture(
        texture: Texture,
        srcX: Float, srcY: Float,
        width: Float, height: Float,
        destX: Float, destY: Float,
        destWidth: Float, destHeight: Float,
        modelMatrix: Matrix?
    ) {
        val tWidth = texture.width
        val tHeight = texture.height
        val tLeft = srcX / tWidth
        val tBottom = srcY / tHeight
        val tRight = (srcX + width) / tWidth
        val tTop = (srcY + height) / tHeight

        if (modelMatrix == null) {
            TextureProgram.use(viewMatrix, texture) {
                draw(
                    destX, destY, destX + destWidth, destY + destHeight,
                    tLeft, tBottom, tRight, tTop
                )
            }
        } else {
            TransformedTextureProgram.use(viewMatrix, modelMatrix, texture, Color.WHITE) {
                draw(
                    destX, destY, destX + destWidth, destY + destHeight,
                    tLeft, tBottom, tRight, tTop
                )
            }
        }

    }

    override fun drawTintedTexture(
        texture: Texture,
        tint: Color,
        srcX: Float, srcY: Float,
        width: Float, height: Float,
        destX: Float, destY: Float,
        destWidth: Float, destHeight: Float,
        modelMatrix: Matrix?
    ) {
        val tWidth = texture.width
        val tHeight = texture.height
        val tLeft = srcX / tWidth
        val tBottom = srcY / tHeight
        val tRight = (srcX + width) / tWidth
        val tTop = (srcY + height) / tHeight

        if (modelMatrix == null) {
            TintedTextureProgram.use(viewMatrix, texture, tint) {
                draw(
                    destX, destY, destX + destWidth, destY + destHeight,
                    tLeft, tBottom, tRight, tTop
                )
            }
        } else {
            TransformedTextureProgram.use(viewMatrix, modelMatrix, texture, tint) {
                draw(
                    destX, destY, destX + destWidth, destY + destHeight,
                    tLeft, tBottom, tRight, tTop
                )
            }
        }
    }

    override fun gradient(vertices: List<Vector2>, colors: Array<Color>) {
        GradientProgram.use(viewMatrix) {
            gradient(vertices, colors)
        }
    }

    override fun hsvGradient(vertices: List<Vector2>, colors: Array<FloatArray>) {
        HSVGradientProgram.use(viewMatrix) {
            gradient(vertices, colors)
        }
    }

    override fun batch(texture: Texture, tint: Color?, modelMatrix: Matrix?, block: TextureBatch.() -> Unit) {
        if (modelMatrix == null && tint == null) {
            TextureProgram.use(viewMatrix, texture) {
                val tWidth = texture.width
                val tHeight = texture.height

                object : TextureBatch {
                    override fun draw(
                        srcX: Float, srcY: Float, width: Float, height: Float,
                        destX: Float, destY: Float, destWidth: Float, destHeight: Float
                    ) {
                        val tLeft = srcX / tWidth
                        val tBottom = srcY / tHeight
                        val tRight = (srcX + width) / tWidth
                        val tTop = (srcY + height) / tHeight
                        this@use.draw(
                            destX, destY, destX + destWidth, destY + destHeight,
                            tLeft, tBottom, tRight, tTop
                        )
                    }
                }.block()
            }
        } else {
            TransformedTextureProgram.use(viewMatrix, modelMatrix ?: Matrix.IDENTITY, texture, tint ?: Color.WHITE) {

                val tWidth = texture.width
                val tHeight = texture.height
                object : TextureBatch {
                    override fun draw(
                        srcX: Float, srcY: Float, width: Float, height: Float,
                        destX: Float, destY: Float, destWidth: Float, destHeight: Float
                    ) {
                        val tLeft = srcX / tWidth
                        val tTop = srcY / tHeight
                        val tRight = (srcX + width) / tWidth
                        val tBottom = (srcY + height) / tHeight
                        this@use.draw(
                            destX, destY, destX + destWidth, destY + destHeight,
                            tLeft, tTop, tRight, tBottom
                        )
                    }
                }.block()
            }
        }
    }

    // endregion Drawing

    // region ==== Factory methods ====

    /**
     * This must be called only once (i.e. only 1 window is allowed when using the WebGLBackend).
     */
    override fun createWindow(width: Int, height: Int): Window {
        if (isWindowInitialised()) {
            throw GlokException("A Glok window has already been created")
        }

        webGLWindow = WebGLWindow()
        return webGLWindow !!
    }

    override fun createTexture(name: String, width: Int, height: Int, pixels: IntArray): Texture {
        throw GlokException("WebGLBackend does not support creating textures from raw pixel data")
    }

    override fun createTexture(name: String, width: Int, height: Int, color: Color): Texture {
        return LoadedWebGLTexture(name, width, height, color)
    }

    override fun resources(path: String, klass: KClass<*>?): Resources {
        return PreLoadedResources(path)
    }

    override fun fileResources(path: String): Resources {
        return WebGLResources(path)
    }

    // endregion Factory methods

    // region ==== Other ====

    override fun reportError(): Boolean {
        val error = gl.getError()
        if (error != 0) {
            log.severe("WebGL Error $error")
            return true
        }
        return false
    }

    override fun reset() {
        gl.enable(BLEND)
        gl.blendFunc(SRC_ALPHA, ONE_MINUS_SRC_ALPHA)
        gl.disable(SCISSOR_TEST)
        gl.colorMask(true, true, true, true)
        reportError()
    }

    override fun pollEvents(timeoutSeconds: Double) {
        // Do nothing. events are handled by adding event listeners to the canvas/html document rather than polling.
    }
    override fun monitorSize(): Pair<Int, Int> {
        return Pair(window.outerWidth, window.outerHeight)
    }

    // endregion Other

    companion object {

        lateinit var canvas: HTMLCanvasElement

        lateinit var gl: WebGLRenderingContext

        var webGLWindow: WebGLWindow? = null

        private fun isWindowInitialised() = webGLWindow != null

        // region ==== Events ====
        internal fun processMouseMove(e: DomMouseEvent) {
            val windowListener = webGLWindow?.windowListener ?: return
            windowListener.mouseMove(e.offsetX.toInt(), e.offsetY.toInt())
            e.stopImmediatePropagation()
            e.preventDefault()
        }

        internal fun processContextMenu(e: DomMouseEvent) {
            val windowListener = webGLWindow?.windowListener ?: return
            windowListener.mouseButton(1, true, mods(e))
            e.stopImmediatePropagation()
            e.preventDefault()
        }

        internal fun processMouseDown(e: DomMouseEvent) {
            val windowListener = webGLWindow?.windowListener ?: return
            //log.info("mouseDown begin")
            windowListener.mouseButton(convertButton(e.button), true, mods(e))
            e.stopImmediatePropagation()
            e.preventDefault()
            //log.info("mouseDown end")
        }

        internal fun processMouseUp(e: DomMouseEvent) {
            val windowListener = webGLWindow?.windowListener ?: return
            //log.info("mouseUp begin")
            windowListener.mouseButton(convertButton(e.button), false, mods(e))
            e.stopImmediatePropagation()
            e.preventDefault()
            //log.info("mouseUp end")
        }

        internal fun processWheel(e: WheelEvent) {
            val windowListener = webGLWindow?.windowListener ?: return
            // log.info("scroll mode : ${e.deltaMode}")
            windowListener.scroll(- e.deltaX.toFloat(), - e.deltaY.toFloat())
            e.stopImmediatePropagation()
            e.preventDefault()
        }

        internal fun processKeyDown(e: KeyboardEvent) {
            val windowListener = webGLWindow?.windowListener ?: return
            //log.info("keyDown begin")
            val jsKey = e.key
            val jsKeyCode = e.code
            val glokKey = keyMapping[jsKeyCode] ?: Key.UNKNOWN
            windowListener.key(glokKey, if (e.repeat) 2 else 1, scanCode(e), mods(e))

            // Is this a typed character (e.g. letter, number, punctuation etc.)
            if (! e.ctrlKey && ! e.altKey && ! e.metaKey && jsKey.length == 1) {
                //log.info("Key Typed : $jsKey")
                windowListener.typed(jsKey[0].code)
                e.preventDefault()
            } else {
                // Which other keys should be prevented from performing their default action?
                // e.g. F5 (refresh), Ctrl+L, Ctrl+T should all be let through.
                // However, Tab, arrow keys, pageUp/pageDown etc. should be intercepted.
                if (glokKey in preventDefaultKey) {
                    e.preventDefault()
                }
            }
            e.stopImmediatePropagation()
            //log.info("keyDown end")
        }

        internal fun processKeyUp(e: KeyboardEvent) {
            val windowListener = webGLWindow?.windowListener ?: return
            //log.info("keyUp begin")
            val jsKey = e.key
            val glokKey = keyMapping[jsKey] ?: Key.UNKNOWN
            windowListener.key(glokKey, 0, scanCode(e), mods(e))
            e.stopImmediatePropagation()
            //log.info("keyUp end")
        }

        internal fun processResize() {
            //log.info("resize begin")
            val windowListener = webGLWindow?.windowListener ?: return
            val container = canvas.parentElement ?: return
            canvas.width = container.clientWidth
            canvas.height = container.clientHeight
            //log.info("New size = ${canvas.width} x ${canvas.height}")
            windowListener.resize(canvas.width, canvas.height)
            //log.info("resize end")
        }

        private fun scanCode(e: KeyboardEvent): Int = e.keyCode

        private fun mods(e: DomMouseEvent): Int {
            var result = 0
            if (e.altKey) result += Event.MOD_ALT
            if (e.ctrlKey) result += Event.MOD_CONTROL
            if (e.shiftKey) result += Event.MOD_SHIFT
            if (e.metaKey) result += Event.MOD_SUPER
            return result
        }

        private fun mods(e: KeyboardEvent): Int {
            var result = 0
            if (e.altKey) result += Event.MOD_ALT
            if (e.ctrlKey) result += Event.MOD_CONTROL
            if (e.shiftKey) result += Event.MOD_SHIFT
            if (e.metaKey) result += Event.MOD_SUPER
            return result
        }

        private fun convertButton(domButton: Short): Int {
            return when (domButton) {
                1.toShort() -> 2
                2.toShort() -> 1
                else -> domButton.toInt()
            }
        }

        // endregion Events
    }

}

private val preventDefaultKey = setOf(
    Key.TAB, Key.LEFT, Key.RIGHT, Key.UP, Key.DOWN, Key.PAGE_UP, Key.PAGE_DOWN, Key.HOME, Key.END,
    Key.ENTER, Key.DELETE, Key.BACKSPACE, Key.MENU
)
